/**
 * CEC-565. Los tres KPI de la oportunidad -- Importe, Gasto y Margen Esperado -- se pintan
 * siempre en la misma divisa, porque el backend ya se los entrega convertidos a la moneda de
 * referencia del sistema.
 *
 * El literal es fijo a proposito: el ticket pide anadir la divisa sin meter logica dinamica en
 * la vista. Si algun dia la moneda de referencia deja de ser el dolar, se cambia aqui.
 */
export const OPPORTUNITY_KPI_CURRENCY_LABEL = 'USD';

export interface OpportunityKpiAmounts {
  revenue: number;
  expenses: number;
  margin: number;
}

/**
 * El margen es SIEMPRE la resta de los otros dos. No se lee de `expectedRevenue`: ese campo
 * se podia teclear y lo guardado no se volvia a mostrar nunca, porque la pantalla repinta el
 * calculo. Por eso este ticket le quita la edicion.
 */
export function resolveOpportunityKpiAmounts(opportunity: any): OpportunityKpiAmounts {
  const revenue = toAmount(opportunity?.totalRevenue ?? opportunity?.revenue);
  const expenses = toAmount(opportunity?.totalExpenses ?? opportunity?.expenses);

  return { revenue, expenses, margin: revenue - expenses };
}

/**
 * Mantiene el simbolo y el formato es-ES que ya se pintaban, y le anade la divisa. El cero es
 * un valor valido y se pinta, no se oculta.
 */
export function formatOpportunityKpi(value: number): string {
  const amount = value.toLocaleString('es-ES', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return `$${amount} ${OPPORTUNITY_KPI_CURRENCY_LABEL}`;
}

function toAmount(value: unknown): number {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }

  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  return 0;
}

/**
 * CEC-565, decision 4: al pasar el raton por un KPI hay que poder ver con que se convirtio y
 * de que fecha, para que un numero que chirria se pueda comprobar sin abrir un ticket, y para
 * que una tasa de hace seis meses se vea.
 *
 * Se construye con lo que la pantalla ya tiene -- las cotizaciones de la oportunidad -- porque
 * la tasa se aplica por cotizacion y con la fecha de SU documento. El valor numerico de la
 * tasa no viaja hoy en la API de la oportunidad; la fecha, que es lo que permite comprobarla,
 * si.
 */
export function buildOpportunityKpiHint(budgets: any[] | null | undefined, currencies: any[] | null | undefined): string {
  const header = `Importes en ${OPPORTUNITY_KPI_CURRENCY_LABEL}.`;
  const counted = (budgets ?? []).filter(countsTowardsKpi);

  if (!counted.length) {
    return header;
  }

  // Se compone en UNA linea: el tooltip de ng-zorro se pinta en un overlay fuera de este
  // componente, asi que ni los saltos de linea ni un estilo propio llegarian hasta el.
  const detail = counted
    .map((budget) => {
      const code = budget?.budgetCode || budget?.name || 'Cotizacion';
      const currency = resolveCurrencyLabel(budget?.currencyId, currencies);
      const date = formatDocumentDate(budget?.createdOn);

      return date ? `${code} (${currency}, ${date})` : `${code} (${currency})`;
    })
    .join(', ');

  return `${header} Cada cotizacion se convierte con la tasa vigente en su fecha: ${detail}.`;
}

/**
 * CEC-565. Las mismas cotizaciones que entran en el KPI: activa, no borrada y no cancelada
 * (criterio de CEC-247 y CEC-255). Sin este filtro el tooltip nombraba documentos que no se
 * habian convertido -- se vio en el lead 204 de DEV, que lista una cotizacion inactiva.
 *
 * Una cotizacion que no declara las banderas se cuenta: es mejor nombrar una de mas que
 * esconder una que si entro.
 */
function countsTowardsKpi(budget: any): boolean {
  return budget?.isActive !== false && budget?.delete !== true && !budget?.dateCancelled;
}

function resolveCurrencyLabel(currencyId: unknown, currencies: any[] | null | undefined): string {
  if (currencyId === null || currencyId === undefined) {
    return 'sin moneda';
  }

  const match = currencies?.find((currency) => currency?.id === currencyId);

  return match?.isoCode || match?.code || match?.name || 'sin moneda';
}

/**
 * Se formatea a partir del texto ISO, sin construir un Date: pasar por la zona horaria del
 * navegador puede correr la fecha un dia, y la fecha del documento es justo lo que se enseña
 * para poder comprobar la tasa.
 */
function formatDocumentDate(value: unknown): string | null {
  const iso = typeof value === 'string' ? value : value instanceof Date ? value.toISOString() : null;
  const match = iso?.match(/^(\d{4})-(\d{2})-(\d{2})/);

  return match ? `${match[3]}/${match[2]}/${match[1]}` : null;
}
